import React, { useState } from 'react';
import { WeighingTicketData } from '@/components/WeighingTicket';
import { Ticket } from '@/services/ticketsService';
import { generatePrintHTML, generatePDF, generateEmailTemplate } from '@/utils/ticketUtils';
import EmailModal from './EmailModal';

interface TicketActionsProps {
  ticketData?: WeighingTicketData;
  ticket?: Ticket;
  onPrint?: () => void;
  onExport?: () => void;
  onEmail?: () => void;
  className?: string;
  showLabels?: boolean;
  variant?: 'default' | 'compact' | 'minimal';
}

const TicketActions: React.FC<TicketActionsProps> = ({
  ticketData,
  ticket,
  onPrint,
  onExport,
  onEmail,
  className = '',
  showLabels = true,
  variant = 'default'
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipients: '',
    subject: '',
    message: '',
    cc: '',
    bcc: ''
  });

  // Déterminer les données du ticket à utiliser
  const currentTicketData = ticketData || (ticket ? {
    id: ticket.id.toString(),
    code: ticket.reference,
    grossWeight: ticket.poids_brut ? parseFloat(ticket.poids_brut.toString()) : 0,
    tareWeight: ticket.poids_tare ? parseFloat(ticket.poids_tare.toString()) : 0,
    netWeight: ticket.poids_net ? parseFloat(ticket.poids_net.toString()) : 0,
    vehicle: ticket.camion_immatriculation || '',
    driver: ticket.chauffeur_nom || '',
    product: ticket.produit_nom || '',
    site: 'Site principal',
    tenant: 'Tenant principal',
    grossAt: ticket.date_heure || new Date().toISOString(),
    tareAt: ticket.updated_at || new Date().toISOString(),
    grossUser: 'Utilisateur actuel',
    tareUser: 'Utilisateur actuel',
    analysisUser: 'Utilisateur actuel',
    observations: ticket.observations || '',
    weighingDate: ticket.date_heure ? new Date(ticket.date_heure).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
    campaignYear: new Date().getFullYear().toString(),
    ticketNumber: ticket.reference || 'NUMEROTICK',
    supplierIdentity: ticket.fournisseur_nom || 'NOM DE L\'ENTITE',
    clientCode: ticket.client_nom || 'XXXX-XXXX',
    exporterCode: ticket.exportateur_nom || '01',
    productNature: '',
    productOrigin: ticket.provenance || '',
    bushLotNumber: ticket.numero_lot || '',
    trailerRegistration: '',
    transferSheetNumber: (ticket as any).numero_transfert || 'N° FICHE',
    transporterName: '',
    destination: ticket.destination || '',
    provenance: ticket.provenance || '',
    stickerNumber: 'N° STICKER',
    firstWeighingWeight: ticket.poids_brut ? parseFloat(ticket.poids_brut.toString()) : undefined,
    firstWeighingDateTime: ticket.date_heure || undefined,
    secondWeighingWeight: ticket.poids_tare ? parseFloat(ticket.poids_tare.toString()) : undefined,
    secondWeighingDateTime: ticket.updated_at || undefined,
    humidityRateKOR: 'VALEUR % KOR : Valeur KOR',
    qualityRefraction: 0,
    packagingType: 'SACS 50KG',
    humidityRefraction: undefined,
    bagWeight: 50,
    sacsCount: {
      nb_sacs_declares: 0,
      nb_sacs_sains: 0,
      nb_sacs_recond: 0,
      nb_sacs_humides: 0,
      nb_sacs_dechires: 0,
      nb_sacs_rejetes: 0,
      nb_sacs_acceptes: 0
    },
    qualityAnalysis: undefined,
    weighingAgentSignature: 'Agent Peseur',
    clientSupplierSignature: 'Client / Fournisseur'
  } as WeighingTicketData : undefined);

  const handlePrint = async () => {
    if (!currentTicketData) return;
    
    try {
      const printHTML = generatePrintHTML(currentTicketData);
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) {
        alert('Impossible d\'ouvrir la fenêtre d\'impression. Veuillez autoriser les popups.');
        return;
      }

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
      
      onPrint?.();
    } catch (error) {
      console.error('❌ Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression. Veuillez réessayer.');
    }
  };

  const handleExport = async () => {
    if (!currentTicketData) return;
    
    try {
      await generatePDF(currentTicketData);
      onExport?.();
    } catch (error) {
      console.error('❌ Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
  };

  const handleEmail = () => {
    if (!currentTicketData) return;
    
    setEmailData({
      recipients: '',
      subject: `Ticket de Pesée - ${currentTicketData.code}`,
      message: generateEmailTemplate(currentTicketData),
      cc: '',
      bcc: ''
    });
    setShowEmailModal(true);
    onEmail?.();
  };

  const handleEmailSent = () => {
    setShowEmailModal(false);
  };

  if (!currentTicketData) {
    return null;
  }

  const baseClasses = "flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  const getVariantClasses = () => {
    switch (variant) {
      case 'compact':
        return {
          button: "px-3 py-2 text-sm rounded-lg",
          icon: "mr-1",
          text: showLabels ? "text-xs" : "hidden"
        };
      case 'minimal':
        return {
          button: "p-2 rounded-lg",
          icon: "mr-0",
          text: "hidden"
        };
      default:
        return {
          button: "px-4 py-2 rounded-lg",
          icon: "mr-2",
          text: showLabels ? "text-sm" : "hidden"
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <>
      <div className={`flex space-x-2 ${className}`}>
        <button
          onClick={handlePrint}
          className={`${baseClasses} ${variantClasses.button} bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500`}
          title="Imprimer le ticket"
        >
          <i className={`fas fa-print ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Imprimer</span>
        </button>

        <button
          onClick={handleExport}
          className={`${baseClasses} ${variantClasses.button} bg-green-600 hover:bg-green-700 text-white focus:ring-green-500`}
          title="Exporter en PDF"
        >
          <i className={`fas fa-file-pdf ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Exporter PDF</span>
        </button>

        <button
          onClick={handleEmail}
          className={`${baseClasses} ${variantClasses.button} bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500`}
          title="Envoyer par email"
        >
          <i className={`fas fa-envelope ${variantClasses.icon}`}></i>
          <span className={variantClasses.text}>Email</span>
        </button>
      </div>

      {showEmailModal && (
        <EmailModal
          ticketData={currentTicketData}
          emailData={emailData}
          onEmailDataChange={setEmailData}
          onSend={handleEmailSent}
          onClose={() => setShowEmailModal(false)}
        />
      )}
    </>
  );
};

export default TicketActions;
